import {
  Button,
  ButtonGroup,
  IconButton,
  Panel,
  Tooltip,
  Whisper,
  useToaster,
} from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import TrashIcon from "@rsuite/icons/Trash";
import AddOutlineIcon from "@rsuite/icons/AddOutline";
import axios from "axios";
import { getCookies } from "cookies-next";
import { config } from "../../data/config";
import { useEffect, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

// Your custom components
import TissText from "./TissText";
import TissImage from "./TissImage";
import TissHeading from "./TissHeading";
import TissMovie from "./TissMovie";
import TissCatalog from "./TissCatalog";
import TissSlider from "./TissSlider";
import TissGallery from "./TissGallery";
import TissList from "./TissList";
import TissTable from "./TissTable";
import TissChart from "./TissChart";
import TissCounter from "./TissCounter";
import TissButton from "./TissButton";
import TissArticleSlider from "./TissArticleSlider";
import TissMusic from "./TissMusic";

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

export default function TissEditor({ component, parent }: any) {
  const { t } = useTranslation();
  const toaster = useToaster();
  const [data, setData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showPalette, setShowPalette] = React.useState(false);
  const [draggedComponent, setDraggedComponent] = React.useState<string | null>(null);
  const [dragOverContainer, setDragOverContainer] = React.useState<number | null>(null);
  const [previewMode, setPreviewMode] = React.useState(false);
  const [activeLang, setActiveLang] = React.useState<LanguageCode>('en');

  const languages: Array<{ code: LanguageCode; label: string }> = [
    { code: 'ku', label: 'Kurdish' },
    { code: 'fa', label: 'فارسی' },
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'العربية' },
    { code: 'tr', label: 'Türkçe' }
  ];

  const cookie = getCookies();

  const loaditem = async () => {
    setLoading(true);
    try {
      const path = `${config.url}/v1/dashboard/container/getlist/${parent}`;
      const res = await axios.get(path, {
        headers: { Authorization: "Bearer " + cookie["token"] },
      });
      setData(res.data.message || []);
    } catch (error: any) {
      toaster.push(
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          خطا در بارگذاری محتوا
        </div>,
        { placement: "topCenter", duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  };

  const addContainer = async (mode: string) => {
    try {
      const path = `${config.url}/v1/dashboard/container`;
      await axios.post(
        path,
        { parent, component, mode },
        { headers: { Authorization: "Bearer " + cookie["token"] } }
      );
      loaditem();
      toaster.push(
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          {t("Section added successfully")}
        </div>,
        { placement: "topCenter", duration: 2000 }
      );
    } catch (error) {
      toaster.push(
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          {t("Error creating section")}
        </div>,
        { placement: "topCenter", duration: 3000 }
      );
    }
  };

  const removeContainer = async (id: number) => {
    const result = await Swal.fire({
      title: t("Are you sure?"),
      text: t("This action cannot be undone."),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, delete it!"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      const path = `${config.url}/v1/dashboard/container/${id}`;
      await axios.delete(path, {
        headers: { Authorization: "Bearer " + cookie["token"] },
      });
      loaditem();
      Swal.fire({
        icon: "success",
        title: t("Section deleted successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("Error deleting section"),
      });
    }
  };

  const deleteitem = async (id: number) => {
    const result = await Swal.fire({
      title: t("Are you sure?"),
      text: t("Do you want to delete this component?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t("Yes, delete it!"),
      cancelButtonText: t("Cancel"),
    });

    if (!result.isConfirmed) return;

    try {
      const path = `${config.url}/v1/dashboard/component/${id}`;
      await axios.delete(path, {
        headers: { Authorization: "Bearer " + cookie["token"] },
      });
      loaditem();
      Swal.fire({
        icon: "success",
        title: t("Component deleted successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("Error deleting component"),
      });
    }
  };

  const updateItemComponent = async (cols: number, id: number) => {
    try {
      const path = `${config.url}/v1/dashboard/container/colsupdate/${id}`;
      await axios.post(
        path,
        { cols },
        { headers: { Authorization: "Bearer " + cookie["token"] } }
      );
      loaditem();
    } catch (error) {
      toaster.push(
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          خطا در بروزرسانی سایز
        </div>,
        { placement: "topCenter", duration: 3000 }
      );
    }
  };

  const arrowupContainer = async (id: number) => {
    try {
      const path = `${config.url}/v1/dashboard/container/arrowup/${id}`;
      await axios.post(path, {}, { headers: { Authorization: "Bearer " + cookie["token"] } });
      loaditem();
    } catch (error) {}
  };

  const arrowdownContainer = async (id: number) => {
    try {
      const path = `${config.url}/v1/dashboard/container/arrowdown/${id}`;
      await axios.post(path, {}, { headers: { Authorization: "Bearer " + cookie["token"] } });
      loaditem();
    } catch (error) {}
  };

  const arrowup = async (id: number) => {
    try {
      const path = `${config.url}/v1/dashboard/component/arrowup/${id}`;
      await axios.post(path, {}, { headers: { Authorization: "Bearer " + cookie["token"] } });
      loaditem();
    } catch (error) {}
  };

  const arrowdown = async (id: number) => {
    try {
      const path = `${config.url}/v1/dashboard/component/arrowdown/${id}`;
      await axios.post(path, {}, { headers: { Authorization: "Bearer " + cookie["token"] } });
      loaditem();
    } catch (error) {}
  };

  const addComponentToContainer = async (containerId: number, mode: string) => {
    try {
      const path = `${config.url}/v1/dashboard/component`;
      await axios.post(
        path,
        { parent: containerId, component: "container", mode },
        { headers: { Authorization: "Bearer " + cookie["token"] } }
      );
      loaditem();
      toaster.push(
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
          کامپوننت با موفقیت اضافه شد
        </div>,
        { placement: "topCenter", duration: 2000 }
      );
    } catch (error) {
      toaster.push(
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
          خطا در افزودن کامپوننت
        </div>,
        { placement: "topCenter", duration: 3000 }
      );
    }
  };

  const handleDragStart = (e: React.DragEvent, mode: string) => {
    setDraggedComponent(mode);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", mode);
  };

  const handleDragOver = (e: React.DragEvent, containerId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverContainer(containerId);
  };

  const handleDragLeave = () => {
    setDragOverContainer(null);
  };

  const handleDrop = (e: React.DragEvent, containerId: number) => {
    e.preventDefault();
    const mode = e.dataTransfer.getData("text/plain") || draggedComponent;
    if (mode) {
      addComponentToContainer(containerId, mode);
    }
    setDraggedComponent(null);
    setDragOverContainer(null);
  };

  const Gridoption = (cols: number) => {
    switch (cols) {
      case 3:
        return "sm:col-span-4 col-span-12";
      case 2:
        return "sm:col-span-6 col-span-12";
      case 1:
        return "sm:col-span-12 col-span-12";
      default:
        return "sm:col-span-12 col-span-12";
    }
  };

  useEffect(() => {
    loaditem();
  }, [parent]);

  // Preview Mode - Clean view without any buttons
  if (previewMode) {
    return (
      <div className="w-full space-y-6 relative" dir="rtl" style={{ zoom: 1, transform: 'scale(1)' }}>
        {/* Preview Mode Header */}
        <div className="sticky top-0 z-50 bg-white border-b-2 border-gray-200 shadow-sm mb-6">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 rounded-lg p-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-800">{t('Preview Mode')}</h2>
                <p className="text-xs text-gray-500">{t('Viewing all sections without edit controls')}</p>
              </div>
            </div>
            <button
              onClick={() => setPreviewMode(false)}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-md hover:shadow-lg"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('Exit Preview')}
            </button>
          </div>
        </div>

        {/* Preview Content - Clean view without any buttons, no scaling */}
        <div className="space-y-6 preview-mode" style={{ zoom: 1, transform: 'none' }}>
          <style>{`
            .preview-mode {
              zoom: 1 !important;
              transform: none !important;
            }
            .preview-mode * {
              zoom: 1 !important;
              transform: none !important;
            }
            /* Hide edit buttons only - not content buttons */
            .preview-mode .absolute button[title*="Edit"],
            .preview-mode .absolute button[title*="edit"],
            .preview-mode button[title*="Edit"][class*="absolute"],
            .preview-mode button[title*="edit"][class*="absolute"],
            .preview-mode .group:hover .absolute button,
            .preview-mode .opacity-0.group-hover\\:opacity-100 button,
            .preview-mode button[class*="hover:scale"][title*="Edit"],
            .preview-mode button[class*="hover:scale"][title*="edit"] {
              display: none !important;
            }
            /* Remove transforms from content elements but keep functionality */
            .preview-mode img,
            .preview-mode div:not(.absolute),
            .preview-mode section {
              transform: none !important;
              scale: 1 !important;
            }
            /* Ensure content buttons are visible */
            .preview-mode a button,
            .preview-mode button:not([title*="Edit"]):not([title*="edit"]):not(.absolute) {
              display: inline-block !important;
              transform: none !important;
            }
          `}</style>
          <div className="grid grid-cols-12 gap-4">
            {data.map((item: any) => (
              <div key={item.id} className={Gridoption(item.cols)}>
                <div className="bg-white">
                  {item.toCompoennt && item.toCompoennt.length > 0 ? (
                    <div className="space-y-4">
                      {item.toCompoennt.map((p: any) => (
                        <div key={p.id} className="w-full">
                          <PreviewComponent key={`${p.id}-${activeLang}`} data={p} componentname={p.mode} activeLang={activeLang} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
                      <p className="text-sm">{t('Empty section')}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 relative" dir="rtl">
      {/* Language Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-gray-700">زبان / Language:</span>
            <div className="flex gap-2 flex-wrap">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setActiveLang(lang.code)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeLang === lang.code
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Component Palette */}
      <div className="fixed bottom-6 left-6 z-50">
        <div className="relative">
          {/* Toggle Button */}
          <button
            onClick={() => setShowPalette(!showPalette)}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-110"
            title="افزودن کامپوننت"
          >
            <AddOutlineIcon style={{ fontSize: 24 }} />
          </button>

          {/* Component Palette */}
          {showPalette && (
            <div className="absolute bottom-16 left-0 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-80 max-h-[600px] overflow-y-auto">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                <h3 className="font-bold text-gray-800">کامپوننت‌ها</h3>
                <button
                  onClick={() => setShowPalette(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <ComponentPalette onDragStart={handleDragStart} />
            </div>
          )}
        </div>
      </div>

      {/* Editor Grid */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 font-medium">در حال بارگذاری محتوا...</p>
          </div>
        </div>
      ) : data.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="bg-blue-100 rounded-full p-4 mb-4">
              <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("No sections available")}</h3>
            <p className="text-gray-600 mb-6">{t("Add a new section to get started")}</p>
            <Button
              size="lg"
              color="blue"
              appearance="primary"
              onClick={() => addContainer("container")}
              startIcon={<PlusIcon />}
              className="px-6"
            >
              {t("Add New Section")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-4">
            {data.map((item: any, index: number) => (
              <div key={item.id} className={Gridoption(item.cols)}>
                <div
                  className={`
                    bg-white rounded-xl shadow-sm border-2 transition-all
                    ${dragOverContainer === item.id ? 'border-blue-500 bg-blue-50 shadow-lg' : 'border-gray-200 hover:shadow-md'}
                  `}
                  onDragOver={(e) => handleDragOver(e, item.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, item.id)}
                >
                  {/* Container Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2.5 border-b border-gray-200">
                    <div className="flex items-center justify-between gap-3">
                      {/* Left Side - Size and Label */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">
                          {t("Section")} {index + 1}
                        </span>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-300 p-0.5">
                          <Whisper placement="top" speaker={<Tooltip>{t("Full Width")}</Tooltip>}>
                            <button
                              onClick={() => updateItemComponent(1, item.id)}
                              className={`p-1.5 rounded transition-colors ${
                                item.cols === 1
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                              title={t("Full Width")}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                              </svg>
                            </button>
                          </Whisper>
                          <Whisper placement="top" speaker={<Tooltip>{t("Half Width")}</Tooltip>}>
                            <button
                              onClick={() => updateItemComponent(2, item.id)}
                              className={`p-1.5 rounded transition-colors ${
                                item.cols === 2
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                              title={t("Half Width")}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 4a1 1 0 011-1h6a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM13 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                              </svg>
                            </button>
                          </Whisper>
                          <Whisper placement="top" speaker={<Tooltip>{t("One Third Width")}</Tooltip>}>
                            <button
                              onClick={() => updateItemComponent(3, item.id)}
                              className={`p-1.5 rounded transition-colors ${
                                item.cols === 3
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                              }`}
                              title={t("One Third Width")}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 4a1 1 0 011-1h4a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM9 4a1 1 0 011-1h4a1 1 0 011 1v12a1 1 0 01-1 1h-4a1 1 0 01-1-1V4zM15 4a1 1 0 011-1h1a1 1 0 011 1v12a1 1 0 01-1 1h-1a1 1 0 01-1-1V4z" />
                              </svg>
                            </button>
                          </Whisper>
                        </div>
                      </div>
                      
                      {/* Right Side - Actions */}
                      <div className="flex items-center gap-1">
                        <Whisper placement="top" speaker={<Tooltip>حرکت به بالا</Tooltip>}>
                          <button
                            onClick={() => arrowupContainer(item.id)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="حرکت به بالا"
                          >
                            <ArrowUpLineIcon style={{ fontSize: 16 }} />
                          </button>
                        </Whisper>
                        <Whisper placement="top" speaker={<Tooltip>حرکت به پایین</Tooltip>}>
                          <button
                            onClick={() => arrowdownContainer(item.id)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="حرکت به پایین"
                          >
                            <ArrowDownLineIcon style={{ fontSize: 16 }} />
                          </button>
                        </Whisper>
                        <div className="h-4 w-px bg-gray-300 mx-1"></div>
                        <Whisper placement="top" speaker={<Tooltip>{t("Delete Section")}</Tooltip>}>
                          <button
                            onClick={() => removeContainer(item.id)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title={t("Delete Section")}
                          >
                            <TrashIcon style={{ fontSize: 16 }} />
                          </button>
                        </Whisper>
                      </div>
                    </div>
                  </div>

                  {/* Container Content */}
                  <div className="p-4 min-h-[100px]">
                    {item.toCompoennt && item.toCompoennt.length > 0 ? (
                      <div className="space-y-3">
                        {item.toCompoennt.map((p: any, compIndex: number) => (
                          <div
                            key={p.id}
                            className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:border-blue-300 transition-colors"
                          >
                            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {t(p.mode) || p.mode}
                                </span>
                              </div>
                              <div className="flex items-center gap-0.5">
                                <Whisper placement="top" speaker={<Tooltip>بالا</Tooltip>}>
                                  <button
                                    onClick={() => arrowup(p.id)}
                                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="بالا"
                                  >
                                    <ArrowUpLineIcon style={{ fontSize: 14 }} />
                                  </button>
                                </Whisper>
                                <Whisper placement="top" speaker={<Tooltip>پایین</Tooltip>}>
                                  <button
                                    onClick={() => arrowdown(p.id)}
                                    className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="پایین"
                                  >
                                    <ArrowDownLineIcon style={{ fontSize: 14 }} />
                                  </button>
                                </Whisper>
                                <div className="h-3 w-px bg-gray-300 mx-0.5"></div>
                                <Whisper placement="top" speaker={<Tooltip>حذف</Tooltip>}>
                                  <button
                                    onClick={() => deleteitem(p.id)}
                                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="حذف"
                                  >
                                    <TrashIcon style={{ fontSize: 14 }} />
                                  </button>
                                </Whisper>
                              </div>
                            </div>
                            <div className="bg-white rounded border border-gray-100 p-3">
                              <ChoiceCom key={`${p.id}-${activeLang}`} data={p} loaditem={loaditem} componentname={p.mode} activeLang={activeLang} />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className={`
                          text-center py-8 border-2 border-dashed rounded-lg transition-all
                          ${dragOverContainer === item.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}
                        `}
                      >
                        <div className="flex flex-col items-center">
                          <div className="bg-gray-200 rounded-full p-3 mb-3">
                            <AddOutlineIcon className="text-gray-400" style={{ fontSize: 24 }} />
                          </div>
                          <p className="text-sm text-gray-500 mb-2">
                            {dragOverContainer === item.id ? 'رها کنید تا کامپوننت اضافه شود' : 'کامپوننت را اینجا بکشید'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Add Section Button and Preview Button */}
          <div className="flex justify-center items-center gap-3 flex-wrap">
            {data.length > 0 && (
              <button
                onClick={() => setPreviewMode(true)}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg p-3 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center transform hover:scale-105"
                title={t('Preview all sections')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}
            <Button
              size="lg"
              color="blue"
              appearance="primary"
              onClick={() => addContainer("container")}
              startIcon={<PlusIcon />}
              className="px-8 py-2 shadow-lg hover:shadow-xl transition-shadow"
            >
              {t("Add New Section")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ======================
// ComponentPalette Component
// ======================
function ComponentPalette({ onDragStart }: { onDragStart: (e: React.DragEvent, mode: string) => void }) {
  const { t } = useTranslation();

  const componentGroups = [
    {
      title: t("Text Content"),
      icon: "📝",
      items: [
        { mode: "text", icon: "/Editor/text.svg", label: t("Text") },
        { mode: "heading", icon: "/Editor/h1.svg", label: t("Heading") },
        { mode: "list", icon: "/Editor/list.svg", label: t("List") },
      ],
    },
    {
      title: t("Media"),
      icon: "🎬",
      items: [
        { mode: "image", icon: "/Editor/image.svg", label: t("Image") },
        { mode: "movie", icon: "/Editor/movie.svg", label: t("Movie") },
        { mode: "music", icon: "/Editor/play.svg", label: t("Music") },
        { mode: "gallery", icon: "/Editor/gallery.svg", label: t("Gallery") },
      ],
    },
    {
      title: t("Data & Statistics"),
      icon: "📊",
      items: [
        { mode: "table", icon: "/Editor/table.svg", label: t("Table") },
        { mode: "chart", icon: "/Editor/chart.svg", label: t("Chart") },
        { mode: "counter", icon: "/Editor/counter.svg", label: t("Statistics") },
        { mode: "catalog", icon: "/Editor/files.svg", label: t("File, Catalog") },
      ],
    },
    {
      title: t("Interactive"),
      icon: "🔗",
      items: [
        { mode: "button", icon: "/Editor/button.svg", label: t("Button Link") },
        { mode: "slider", icon: "/Editor/slider.svg", label: t("Slider") },
        { mode: "articleslider", icon: "/Editor/article.svg", label: t("Article Slider") },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      {componentGroups.map((group) => (
        <div key={group.title} className="space-y-2">
          <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
            <span className="text-lg">{group.icon}</span>
            <h4 className="text-xs font-semibold text-gray-700">{group.title}</h4>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {group.items.map((item) => (
              <div
                key={item.mode}
                draggable
                onDragStart={(e) => onDragStart(e, item.mode)}
                className="flex flex-col items-center p-2 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg cursor-move transition-all transform hover:scale-105"
                title={item.label}
              >
                <img src={item.icon} className="w-6 h-6 mb-1" alt={item.label} />
                <span className="text-[10px] text-center text-gray-700 leading-tight">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ======================
// ChoiceCom Component (Edit Mode)
// ======================
function ChoiceCom({ data, loaditem, componentname, activeLang }: { data: any; loaditem: () => void; componentname: string; activeLang: LanguageCode }) {
  switch (componentname) {
    case "text":
      return <TissText data={data} reload={loaditem} activeLang={activeLang} />;
    case "heading":
      return <TissHeading data={data} reload={loaditem} activeLang={activeLang} />;
    case "image":
      return <TissImage data={data} reload={loaditem} activeLang={activeLang} />;
    case "movie":
      return <TissMovie data={data} reload={loaditem} activeLang={activeLang} />;
    case "music":
      return <TissMusic data={data} reload={loaditem} activeLang={activeLang} />;
    case "catalog":
      return <TissCatalog data={data} reload={loaditem} activeLang={activeLang} />;
    case "slider":
      return <TissSlider data={data} reload={loaditem} activeLang={activeLang} />;
    case "gallery":
      return <TissGallery data={data} reload={loaditem} activeLang={activeLang} />;
    case "list":
      return <TissList data={data} reload={loaditem} activeLang={activeLang} />;
    case "table":
      return <TissTable data={data} reload={loaditem} activeLang={activeLang} />;
    case "chart":
      return <TissChart data={data} reload={loaditem} activeLang={activeLang} />;
    case "counter":
      return <TissCounter data={data} reload={loaditem} activeLang={activeLang} />;
    case "button":
      return <TissButton data={data} reload={loaditem} activeLang={activeLang} />;
    case "articleslider":
      return <TissArticleSlider data={data} reload={loaditem} activeLang={activeLang} />;
    default:
      return (
        <div className="text-gray-500 text-sm p-2 text-center bg-yellow-50 border border-yellow-200 rounded">
          کامپوننت نامعتبر: {String(componentname)}
        </div>
      );
  }
}

// ======================
// PreviewComponent - Clean preview without edit buttons
// ======================
function PreviewComponent({ data, componentname, activeLang }: { data: any; componentname: string; activeLang: LanguageCode }) {
  // Force components into preview mode by wrapping them
  // Components will show their preview state (isEditing = false)
  switch (componentname) {
    case "text":
      return <TissText data={data} reload={() => {}} activeLang={activeLang} />;
    case "heading":
      return <TissHeading data={data} reload={() => {}} activeLang={activeLang} />;
    case "image":
      return <TissImage data={data} reload={() => {}} activeLang={activeLang} />;
    case "movie":
      return <TissMovie data={data} reload={() => {}} activeLang={activeLang} />;
    case "music":
      return <TissMusic data={data} reload={() => {}} activeLang={activeLang} />;
    case "catalog":
      return <TissCatalog data={data} reload={() => {}} activeLang={activeLang} />;
    case "slider":
      return <TissSlider data={data} reload={() => {}} activeLang={activeLang} />;
    case "gallery":
      return <TissGallery data={data} reload={() => {}} activeLang={activeLang} />;
    case "list":
      return <TissList data={data} reload={() => {}} activeLang={activeLang} />;
    case "table":
      return <TissTable data={data} reload={() => {}} activeLang={activeLang} />;
    case "chart":
      return <TissChart data={data} reload={() => {}} activeLang={activeLang} />;
    case "counter":
      return <TissCounter data={data} reload={() => {}} activeLang={activeLang} />;
    case "button":
      return <TissButton data={data} reload={() => {}} activeLang={activeLang} />;
    case "articleslider":
      return <TissArticleSlider data={data} reload={() => {}} activeLang={activeLang} />;
    default:
      return (
        <div className="text-gray-500 text-sm p-2 text-center bg-yellow-50 border border-yellow-200 rounded">
          Invalid component: {String(componentname)}
        </div>
      );
  }
}
